import type { PlatformStore } from "@/platform/types";
import { meUserWithOrg, normalizeIdentityUser } from "./authUtils";

export type SessionTransport = {
  loadStore: () => Promise<PlatformStore>;
  request: (
    method: "GET" | "POST",
    url: string,
    body?: unknown,
    token?: string
  ) => Promise<unknown>;
};
export type SessionSnapshot = {
  id: string | null;
  token: string | null;
  businessId: string | null;
  revision: number;
};
type SessionUpdate = { user: any; token: string | null };
const listeners = new Set<(update: SessionUpdate) => void>();
let revision = 0;
let writes: Promise<unknown> = Promise.resolve();
const flights = new Map<string, Promise<SessionSnapshot>>();
const SESSION_ID = "authSessionId";
const NEEDS_ME = "sessionNeedsMe";
let publishedToken: string | null | undefined;

// No React/platform imports: all transports share ONE coordinator, including
// the two Axios instances and native IPC. Tokens never enter query keys/logs.
export function subscribeSessionUpdates(
  listener: (update: SessionUpdate) => void
) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function publish(update: SessionUpdate) {
  publishedToken = update.token;
  listeners.forEach((listener) => listener(update));
}
function exclusive<T>(name: string, action: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(name, action);
  }
  return action();
}
function writeSession<T>(action: () => Promise<T>): Promise<T> {
  const result = writes.then(() => exclusive("trud-session-write", action));
  writes = result.catch(() => undefined);
  return result;
}

export function tokenClaims(token: string | null): Record<string, unknown> {
  try {
    const part = token?.split(".")[1];
    const value = part
      ? JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")))
      : null;
    return value && typeof value === "object" && !Array.isArray(value)
      ? value
      : {};
  } catch {
    return {};
  }
}
export function isIdentityToken(token: string | null) {
  return tokenClaims(token).token_type === "scoped";
}
export class SessionChanged extends Error {
  constructor() {
    super("La sesión cambió. Volvé a intentar en el negocio actual.");
  }
}
export class SessionExpired extends Error {
  constructor() {
    super("La sesión venció. Volvé a iniciar sesión.");
  }
}

export async function sessionSnapshot(
  store: PlatformStore
): Promise<SessionSnapshot> {
  return writeSession(async () => {
    const token = await store.get("token");
    let id = await store.get(SESSION_ID);
    if (token && !id) {
      id = crypto.randomUUID();
      await store.set(SESSION_ID, id);
    }
    return { id, token, businessId: await store.get("businessId"), revision };
  });
}
async function assertCurrent(store: PlatformStore, before: SessionSnapshot) {
  const id = await store.get(SESSION_ID);
  const businessId = await store.get("businessId");
  if (
    revision !== before.revision ||
    id !== before.id ||
    businessId !== before.businessId
  )
    throw new SessionChanged();
}
export async function assertSessionCurrent(
  store: PlatformStore,
  before: SessionSnapshot
) {
  await assertCurrent(store, before);
  const current = tokenClaims(await store.get("token"));
  const original = tokenClaims(before.token);
  for (const key of [
    "sub",
    "actor_id",
    "organization_id",
    "business_id",
    "act",
  ]) {
    if (JSON.stringify(current[key]) !== JSON.stringify(original[key]))
      throw new SessionChanged();
  }
  if (revision !== before.revision) throw new SessionChanged();
}

// /me authorized by A must not publish old permissions after rotation to B.
// Other responses keep the session/identity guard: a command already accepted
// by the server must not become a false failure just because its JWT rotated.
export async function assertResponseCurrent(
  store: PlatformStore,
  before: SessionSnapshot,
  url: string
) {
  await assertSessionCurrent(store, before);
  if (
    /\/auth\/me(?:[/?]|$)/.test(url) &&
    (await store.get("token")) !== before.token
  )
    throw new SessionChanged();
}

export async function clearSession(
  store: PlatformStore,
  expected?: SessionSnapshot
) {
  if (!expected) {
    revision++;
    publish({ user: null, token: null });
  }
  await writeSession(async () => {
    // A failed renewal must never log out a newer login in another tab.
    if (expected) {
      await assertSessionCurrent(store, expected);
      revision++;
      publish({ user: null, token: null });
    }
    // Keep a non-secret tombstone: logout from another tab must also invalidate
    // an in-flight handoff that started without any authenticated session.
    await store.set(SESSION_ID, crypto.randomUUID());
    for (const key of [NEEDS_ME, "token", "refreshToken", "user", "businessId"])
      await store.delete(key);
    if (typeof document !== "undefined")
      document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  });
}

// Called by BOTH login paths (single business and picker). The refresh token
// comes from login, not select-business. Act-as intentionally has no refresh.
export async function saveIdentitySession(
  store: PlatformStore,
  token: string,
  refreshToken: string | undefined,
  user: any,
  expected?: SessionSnapshot
) {
  if (!expected) revision++;
  const ownRevision = revision;
  await writeSession(async () => {
    if (ownRevision !== revision) throw new SessionChanged();
    if (expected) {
      await assertSessionCurrent(store, expected);
      revision++;
    }
    const installedRevision = revision;
    await store.set(SESSION_ID, crypto.randomUUID());
    await store.set("token", token);
    await store.set(
      "businessId",
      String(tokenClaims(token).business_id ?? user.businessId)
    );
    if (refreshToken && !tokenClaims(token).act)
      await store.set("refreshToken", refreshToken);
    else await store.delete("refreshToken");
    await store.delete(NEEDS_ME);
    await store.set("user", JSON.stringify(user));
    document.cookie = `token=${token}; path=/; SameSite=Strict`;
    if (installedRevision !== revision) throw new SessionChanged();
    publish({ user: normalizeIdentityUser(user, token), token });
  });
}

export function errorStatus(error: unknown): number | undefined {
  const e = error as {
    response?: { status?: number };
    status?: number;
    message?: string;
  };
  return (
    e?.response?.status ??
    e?.status ??
    (Number(e?.message?.match(/^HTTP API error: HTTP (\d{3})(?:\b)/)?.[1]) ||
      undefined)
  );
}
function unwrap(value: any): any {
  return value?.data ?? value;
}

// A refresh can commit before /me succeeds. Never run a protected request with
// old UI permissions after reload or during that partially completed rotation.
export async function prepareSession(
  io: SessionTransport
): Promise<SessionSnapshot> {
  const store = await io.loadStore();
  const snapshot = await sessionSnapshot(store);
  const pending = snapshot.id && flights.get(snapshot.id);
  if (pending) return pending;
  if (isIdentityToken(snapshot.token) && (await store.get(NEEDS_ME)))
    return renewSession(io, snapshot, { resumeOnly: true });
  // Another tab may rotate/logout without this tab ever receiving a 401.
  if (snapshot.token !== publishedToken) {
    await writeSession(async () => {
      await assertSessionCurrent(store, snapshot);
      const rawUser = await store.get("user");
      let user: any;
      try {
        user = typeof rawUser === "string" ? JSON.parse(rawUser) : rawUser;
      } catch {
        return;
      }
      if (!snapshot.token || user)
        publish({
          user: snapshot.token
            ? normalizeIdentityUser(user, snapshot.token)
            : null,
          token: snapshot.token,
        });
    });
  }
  return snapshot;
}

async function syncUser(
  io: SessionTransport,
  store: PlatformStore,
  before: SessionSnapshot,
  token: string
) {
  let me: unknown;
  try {
    me = await io.request("GET", "/api/v1/auth/me", undefined, token);
  } catch (error) {
    await assertSessionCurrent(store, before);
    if (errorStatus(error) === 401 || errorStatus(error) === 403) {
      await clearSession(store, before);
      throw new SessionExpired();
    }
    throw new Error(
      "No se pudieron actualizar los permisos. Revisá la conexión y volvé a intentar."
    );
  }
  const user = meUserWithOrg(me);
  if (!user || user.businessId !== tokenClaims(token).business_id)
    throw new Error("No se pudieron actualizar los permisos del negocio.");
  await writeSession(async () => {
    await assertSessionCurrent(store, before);
    const normalized = normalizeIdentityUser(user, token);
    await store.set("user", JSON.stringify(normalized));
    await store.delete(NEEDS_ME);
    await assertSessionCurrent(store, before);
    publish({ user: normalized, token });
  });
}

export async function renewSession(
  io: SessionTransport,
  before: SessionSnapshot,
  options: { resumeOnly?: boolean } = {}
): Promise<SessionSnapshot> {
  const claims = tokenClaims(before.token);
  if (!isIdentityToken(before.token)) throw new SessionExpired();
  const key = before.id ?? "missing-session";
  const existing = flights.get(key);
  if (existing) return existing;
  const pending = exclusive("trud-identity-refresh", async () => {
    const store = await io.loadStore();
    await assertSessionCurrent(store, before);
    if (claims.act) {
      await clearSession(store, before);
      throw new SessionExpired();
    }
    // Another request/tab already rotated the same session while this waited.
    const currentToken = await store.get("token");
    if (
      currentToken &&
      (options.resumeOnly ||
        currentToken !== before.token ||
        (await store.get(NEEDS_ME)))
    ) {
      if (await store.get(NEEDS_ME)) {
        await syncUser(io, store, before, currentToken);
        return sessionSnapshot(store);
      }
      const storedUser = await store.get("user");
      let user: any = storedUser;
      if (typeof user === "string") {
        try {
          user = JSON.parse(user);
        } catch {
          user = null;
        }
      }
      if (user)
        publish({
          user: normalizeIdentityUser(user, currentToken),
          token: currentToken,
        });
      return sessionSnapshot(store);
    }
    const refreshToken = await store.get("refreshToken");
    if (!refreshToken || typeof claims.business_id !== "string") {
      await clearSession(store, before);
      throw new SessionExpired();
    }
    let refreshed: any;
    try {
      refreshed = unwrap(
        await io.request("POST", "/api/v1/auth/refresh", {
          refresh_token: refreshToken,
          business_id: claims.business_id,
        })
      );
    } catch (error) {
      await assertSessionCurrent(store, before);
      if (errorStatus(error) === 401 || errorStatus(error) === 403) {
        await clearSession(store, before);
        throw new SessionExpired();
      }
      // A network/provider outage is not revocation. Keep credentials to retry.
      // Do not forward Axios config: its body contains the refresh credential.
      throw new Error(
        "No se pudo renovar la sesión. Revisá la conexión y volvé a intentar."
      );
    }
    await assertSessionCurrent(store, before);
    const nextClaims = tokenClaims(refreshed?.token ?? null);
    if (
      typeof refreshed?.token !== "string" ||
      typeof refreshed.refresh_token !== "string" ||
      !refreshed.refresh_token ||
      refreshed.business_id !== claims.business_id ||
      nextClaims.token_type !== "scoped" ||
      nextClaims.act ||
      ["sub", "actor_id", "organization_id", "business_id"].some(
        (k) => nextClaims[k] !== claims[k]
      )
    ) {
      throw new Error("La renovación devolvió una sesión incompatible.");
    }
    // Persist rotated credentials before /me: if /me is unavailable, reusing
    // the previous refresh would revoke the whole session at Identity.
    await writeSession(async () => {
      await assertSessionCurrent(store, before);
      await store.set(NEEDS_ME, "true");
      await store.set("refreshToken", refreshed.refresh_token);
      await store.set("token", refreshed.token);
      document.cookie = `token=${refreshed.token}; path=/; SameSite=Strict`;
      await assertSessionCurrent(store, before);
    });
    await syncUser(io, store, before, refreshed.token);
    return sessionSnapshot(store);
  });
  flights.set(key, pending);
  try {
    return await pending;
  } finally {
    if (flights.get(key) === pending) flights.delete(key);
  }
}
